/**
 * Pure helpers backing the workbench Lifecycle tab.
 *
 * The tab has two horizontal sub-tab strips:
 *
 *   1. Design-time  : Snapshots / Auto-Saves / Releases  (snapshot list)
 *   2. Run-time     : Active / Notified / Paused / Retired / Archived
 *
 * Both strips render bracketed counts next to every label. Keeping the
 * partition / count math in a pure module means we can:
 *
 *   * Unit-test the rules without booting React / AppSync mocks.
 *   * Render the same labels on the tenant-wide Overwatch dashboard
 *     without copy-pasting the bucket key list.
 *
 * No imports from React, services, or styles — keep it that way.
 */
import {
  SNAPSHOT_KIND_BASELINE,
  SNAPSHOT_KIND_CHECKPOINT,
  SNAPSHOT_KIND_RELEASE,
  SNAPSHOT_KIND_USER,
} from './snapshotList';

// ---------------------------------------------------------------------------
// Design-time sub-tabs (snapshot / release split)
// ---------------------------------------------------------------------------

export const DESIGN_BUCKET_SNAPSHOTS = 'snapshots';
export const DESIGN_BUCKET_AUTO_SAVES = 'autoSaves';
export const DESIGN_BUCKET_RELEASES = 'releases';

export const DESIGN_BUCKETS = Object.freeze([
  DESIGN_BUCKET_SNAPSHOTS,
  DESIGN_BUCKET_AUTO_SAVES,
  DESIGN_BUCKET_RELEASES,
]);

export const DESIGN_BUCKET_LABELS = Object.freeze({
  [DESIGN_BUCKET_SNAPSHOTS]: 'Snapshots',
  [DESIGN_BUCKET_AUTO_SAVES]: 'Auto-Saves',
  [DESIGN_BUCKET_RELEASES]: 'Releases',
});

/**
 * Map a snapshot ``entry.kind`` to the design-time bucket it belongs
 * in. Returns ``null`` for unknown kinds so the caller can decide
 * whether to drop or land them in a fallback bucket.
 *
 *   * ``USER_SNAPSHOT`` → ``snapshots``  (user-saved checkpoints)
 *   * ``CHECKPOINT``    → ``autoSaves``  (system pre-run / pre-restore)
 *   * ``RELEASE``       → ``releases``
 *   * ``BASELINE``      → ``releases``   (also a release-y artifact)
 */
export function designBucketForSnapshot(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const kind = entry.kind;
  if (kind === SNAPSHOT_KIND_USER) return DESIGN_BUCKET_SNAPSHOTS;
  if (kind === SNAPSHOT_KIND_CHECKPOINT) return DESIGN_BUCKET_AUTO_SAVES;
  if (kind === SNAPSHOT_KIND_RELEASE) return DESIGN_BUCKET_RELEASES;
  if (kind === SNAPSHOT_KIND_BASELINE) return DESIGN_BUCKET_RELEASES;
  return null;
}

/**
 * Partition a flat snapshot list into the three design-time buckets.
 * Order within each bucket is preserved from the input — callers that
 * want newest-first should pre-sort with ``sortSnapshotsNewestFirst``.
 *
 * Always returns objects with all three keys even when a bucket is
 * empty so the consumer can ``buckets[active]?.length`` without
 * defensive checks.
 *
 * @param {Array<object>} snapshots
 * @returns {{
 *   snapshots: object[],
 *   autoSaves: object[],
 *   releases: object[],
 * }}
 */
export function partitionSnapshotsForLifecycle(snapshots) {
  const out = {
    [DESIGN_BUCKET_SNAPSHOTS]: [],
    [DESIGN_BUCKET_AUTO_SAVES]: [],
    [DESIGN_BUCKET_RELEASES]: [],
  };
  if (!Array.isArray(snapshots)) return out;
  for (const entry of snapshots) {
    const bucket = designBucketForSnapshot(entry);
    if (bucket && out[bucket]) out[bucket].push(entry);
  }
  return out;
}

/**
 * @param {Array<object>} snapshots
 * @returns {{ snapshots: number, autoSaves: number, releases: number }}
 */
export function designBucketCounts(snapshots) {
  const part = partitionSnapshotsForLifecycle(snapshots);
  return {
    [DESIGN_BUCKET_SNAPSHOTS]: part[DESIGN_BUCKET_SNAPSHOTS].length,
    [DESIGN_BUCKET_AUTO_SAVES]: part[DESIGN_BUCKET_AUTO_SAVES].length,
    [DESIGN_BUCKET_RELEASES]: part[DESIGN_BUCKET_RELEASES].length,
  };
}

// ---------------------------------------------------------------------------
// Run-time sub-tabs (pool lifecycle status)
// ---------------------------------------------------------------------------

export const RUNTIME_BUCKET_ACTIVE = 'active';
export const RUNTIME_BUCKET_NOTIFIED = 'notified';
export const RUNTIME_BUCKET_PAUSED = 'paused';
export const RUNTIME_BUCKET_RETIRED = 'retired';
export const RUNTIME_BUCKET_ARCHIVED = 'archived';

export const RUNTIME_BUCKETS = Object.freeze([
  RUNTIME_BUCKET_ACTIVE,
  RUNTIME_BUCKET_NOTIFIED,
  RUNTIME_BUCKET_PAUSED,
  RUNTIME_BUCKET_RETIRED,
  RUNTIME_BUCKET_ARCHIVED,
]);

export const RUNTIME_BUCKET_LABELS = Object.freeze({
  [RUNTIME_BUCKET_ACTIVE]: 'Active',
  [RUNTIME_BUCKET_NOTIFIED]: 'Notified',
  [RUNTIME_BUCKET_PAUSED]: 'Paused',
  [RUNTIME_BUCKET_RETIRED]: 'Retired',
  [RUNTIME_BUCKET_ARCHIVED]: 'Archived',
});

/**
 * The five canonical lifecycle statuses plus their badge tone — kept
 * in lockstep with the backend ``models.pool_lifecycle.LIFECYCLE_BUCKETS``
 * (M2). Tones mirror the Snapshots tab palette so the visual language
 * doesn't fork between design-time and run-time.
 */
export const RUNTIME_BUCKET_TONES = Object.freeze({
  [RUNTIME_BUCKET_ACTIVE]: 'success',     // emerald
  [RUNTIME_BUCKET_NOTIFIED]: 'danger',    // red — surfaces auto-retire risk
  [RUNTIME_BUCKET_PAUSED]: 'warning',     // orange
  [RUNTIME_BUCKET_RETIRED]: 'muted',      // grey — admin-only by default
  [RUNTIME_BUCKET_ARCHIVED]: 'muted',
});

const _STATUS_TO_BUCKET = Object.freeze({
  ACTIVE: RUNTIME_BUCKET_ACTIVE,
  NOTIFIED: RUNTIME_BUCKET_NOTIFIED,
  PAUSED: RUNTIME_BUCKET_PAUSED,
  RETIRED: RUNTIME_BUCKET_RETIRED,
  ARCHIVED: RUNTIME_BUCKET_ARCHIVED,
});

/**
 * Project a pool's API ``lifecycleStatus`` to a frontend bucket key.
 * Falls back to ``active`` for unknown / missing statuses so a stale
 * client doesn't strand a pool in an invisible bucket — the operator
 * still sees it and can act on it.
 *
 * @param {{ lifecycleStatus?: string, status?: string } | null | undefined} pool
 */
export function runtimeBucketForPool(pool) {
  if (!pool || typeof pool !== 'object') return RUNTIME_BUCKET_ACTIVE;
  const raw = (pool.lifecycleStatus || pool.status || '').toString().toUpperCase();
  return _STATUS_TO_BUCKET[raw] || RUNTIME_BUCKET_ACTIVE;
}

/**
 * Partition a flat pool list into the five run-time buckets. Always
 * returns objects with every bucket key present (empty arrays included)
 * so the UI can render zero-state copy without defensive checks.
 *
 * @param {Array<object>} pools
 */
export function partitionPoolsForLifecycle(pools) {
  const out = {
    [RUNTIME_BUCKET_ACTIVE]: [],
    [RUNTIME_BUCKET_NOTIFIED]: [],
    [RUNTIME_BUCKET_PAUSED]: [],
    [RUNTIME_BUCKET_RETIRED]: [],
    [RUNTIME_BUCKET_ARCHIVED]: [],
  };
  if (!Array.isArray(pools)) return out;
  for (const pool of pools) {
    const bucket = runtimeBucketForPool(pool);
    if (out[bucket]) out[bucket].push(pool);
  }
  return out;
}

/**
 * Coerce a ``counts`` payload from the server (or a partition derived
 * locally) into a normalised five-key object. Unknown / malformed
 * input becomes zeros so the UI never renders ``Active (NaN)``.
 *
 * @param {Record<string, unknown> | null | undefined} raw
 */
export function normalizeRuntimeCounts(raw) {
  const out = {
    [RUNTIME_BUCKET_ACTIVE]: 0,
    [RUNTIME_BUCKET_NOTIFIED]: 0,
    [RUNTIME_BUCKET_PAUSED]: 0,
    [RUNTIME_BUCKET_RETIRED]: 0,
    [RUNTIME_BUCKET_ARCHIVED]: 0,
  };
  if (!raw || typeof raw !== 'object') return out;
  for (const key of RUNTIME_BUCKETS) {
    const v = raw[key];
    const n = typeof v === 'string' ? Number(v) : v;
    out[key] = Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  }
  return out;
}

/**
 * The two strips together, in the order they should render — exposed
 * as a single array so a future "all buckets in one strip" tweak only
 * has to change one consumer.
 *
 * @param {{ design: Record<string, number>, runtime: Record<string, number> }} counts
 */
export function buildLifecycleTabStrip(counts) {
  const design = counts?.design || {};
  const runtime = counts?.runtime || {};
  return {
    design: DESIGN_BUCKETS.map((id) => ({
      id,
      label: DESIGN_BUCKET_LABELS[id],
      count: Number(design[id]) || 0,
    })),
    runtime: RUNTIME_BUCKETS.map((id) => ({
      id,
      label: RUNTIME_BUCKET_LABELS[id],
      count: Number(runtime[id]) || 0,
      tone: RUNTIME_BUCKET_TONES[id],
    })),
  };
}

// ---------------------------------------------------------------------------
// Inactivity warning copy
// ---------------------------------------------------------------------------

/**
 * The persistent banner the chat composer shows for ``NOTIFIED`` pools
 * (M5 will wire this through the chat surface; we expose the copy here
 * now so the workbench Lifecycle tab can render the same string in the
 * Notified bucket row, keeping copy in one place).
 *
 * @param {{ daysUntilRetire?: number|string|null } | null | undefined} warning
 */
export function inactivityBannerText(warning) {
  if (!warning || typeof warning !== 'object') return null;
  const raw = warning.daysUntilRetire;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  if (!Number.isFinite(n) || n <= 0) {
    return 'This network has been inactive and will retire automatically. Send a prompt to keep it active.';
  }
  const rounded = Math.max(1, Math.floor(n));
  const dayWord = rounded === 1 ? 'day' : 'days';
  return `Warning: this network has been inactive and will retire in ${rounded} ${dayWord}. Send a prompt to keep it active.`;
}
